import { useState } from 'react';
import { Button, ButtonTheme, GenericAlertPopup } from '@apply/components';
import { AuthorizedDrawUserDto, ApplyApi } from '@apply/api/src/api/ApplyApi';
import { applicationInsights } from '../../../../../../../../../ApplicationInsights';
import { ConfirmV2 } from '@apply/ui';
import LoadPanel from 'devextreme-react/load-panel';

export interface DeleteUserButtonProps {
    user: AuthorizedDrawUserDto;
    onRefresh: () => void;
    onClose?: () => void;
}

export const DeleteUserButton: React.FC<DeleteUserButtonProps> = ({ user, onRefresh, onClose }) => {
    const [deleting, setDeleting] = useState<boolean>(false);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);

    const handleDeleteClick = () => {
        setShowConfirm(true);
        onClose?.();
    };

    const handleConfirm = async () => {
        setShowConfirm(false);
        setDeleting(true);

        try {
            const applyApi = new ApplyApi(applicationInsights);
            const response = await applyApi.deleteDrawAuthorizedUser(user.userId);

            if (response.statusCode === 200) {
                GenericAlertPopup({
                    title: 'Success',
                    children: 'User deleted successfully!',
                    width: '350px',
                });
                onRefresh();
            } else {
                const errorMsg = response.errors?.length > 0 ? response.errors.join(', ') : 'Failed to delete user';
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error('Failed to delete user:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to delete user. Please try again.';
            GenericAlertPopup({
                title: 'Error',
                children: errorMessage,
                width: '350px',
            });
        } finally {
            setDeleting(false);
        }
    };

    const handleCancel = () => {
        setShowConfirm(false);
    };

    return (
        <>
            <Button
                id={`gcDeleteUserButton_${user.userId}`}
                theme={ButtonTheme.LinkRed}
                height='15px'
                handleClick={handleDeleteClick}
                disabled={deleting}
            >
                {deleting ? 'Deleting...' : 'Delete'}
            </Button>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={deleting}
                message='Deleting user...'
            />
            <ConfirmV2
                id='deleteUserConfirm'
                title='Delete User'
                open={showConfirm}
                okLabel='Delete'
                cancelLabel='Cancel'
                width='400px'
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            >
                Are you sure you want to delete {user.displayFirstName} {user.displayLastName}? This will remove them from all assigned properties and they will no longer be able to request draws.
                <br /><br />
            </ConfirmV2>
        </>
    );
};
